import { Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { CollectionStatus, PatientCopay, PatientCopayDetails, ShipmentCollectionStatus } from 'src/app/core/models';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import {
  OrderWizardStatus,
  OutOfPocketMetStatus,
} from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { ApiStoreData } from 'src/app/core/store/utils';
import { FormatCurrencyPipe } from 'src/app/core/utils/pipes';

@Component({
  selector: 'app-patient-responsibility',
  templateUrl: './patient-responsibility.component.html',
  styleUrls: ['./patient-responsibility.component.scss'],
})
export class PatientResponsibilityComponent implements OnInit {
  @Select(OrderWizardDataState.copay) copay$!: Observable<PatientCopay | null>;
  @Select(OrderWizardDataState.hideSummaryStepBillingInfo) hideSummaryStepBillingInfo$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.collectionStatus) collectionStatus$!: Observable<ShipmentCollectionStatus | null>;
  @Select(OrderWizardDataState.skipCollectionForReconsent) skipCollectionForReconsent$!: Observable<boolean>;
  @Select(OrderWizardDataState.updateCopayDeductibleOverridesState)
  updateCopayDeductibleOverridesState$!: Observable<ApiStoreData<null>>;
  @Select(OrderWizardDataState.hasAppliedDeductibleOverrides) hasAppliedDeductibleOverrides$!: Observable<boolean>;
  @Select(OrderWizardDataState.outOfPocket) selectedOutOfPocket$!: Observable<OutOfPocketMetStatus>;

  currencyFormattingPipe = new FormatCurrencyPipe();
  displayCopayDetails: boolean = false;
  copayDetailsTabs: boolean[] = [];
  outOfPocketMetStatus: boolean = false;

  constructor(private store: Store) {}

  ngOnInit() {
    this.copay$.subscribe((patientCopay) => {
      if (patientCopay?.copayDetails) {
        this.copayDetailsTabs = patientCopay?.copayDetails.items.map(() => false);
      }
    });

    this.selectedOutOfPocket$.subscribe((oop) => {
      if (oop?.primaryOutOfPocketMet || oop?.secondaryOutOfPocketMet) {
        this.outOfPocketMetStatus = true;
      }
    });
  }

  getCollectionStatusInfo(cs?: ShipmentCollectionStatus | null): string | undefined {
    if (!cs || !cs.status) return;

    const formattedAmount = this.currencyFormattingPipe.transform(cs.collectionAmount);

    switch (cs.status) {
      case CollectionStatus.PortalPaidBalance:
      case CollectionStatus.AgentCollectedBalance:
        return `Based on previous authorization for this order, you previously paid your balance of
        ${formattedAmount}.`;
      case CollectionStatus.PortalPaidCopay:
      case CollectionStatus.AgentCollectedCopay:
        return `Based on previous authorization for this order, you previously paid an estimated responsibility of
        ${formattedAmount}.`;
      case CollectionStatus.PortalPaidBalanceAndCopay:
      case CollectionStatus.AgentCollectedBalanceAndCopay:
        return `Based on previous authorization for this order, you previously paid a balance and estimated responsibility of
          ${formattedAmount}.`;
      case CollectionStatus.NoCollectionNeeded:
        return 'Based on previous authorization for this order, no collection is needed.';
      case CollectionStatus.ThreeMonthsSplit:
        return `Based on previous authorization for this order, a 3 month split of ${formattedAmount} has been setup for this shipment.`;
      case CollectionStatus.BillPatient:
        return 'We will process the shipment and submit the claim to your insurance.';
      default:
        return;
    }
  }

  getCopayExclusionInfo(c?: PatientCopay | null): string | undefined {
    if (!c) return;

    const formattedAmount = this.currencyFormattingPipe.transform(c.estimatedPatientResponsibility);

    if (c.estimatedPatientResponsibility > 0) {
      return `After collecting your current balance of ${formattedAmount}, we will process the shipment and submit the claim to your insurance.`;
    } else {
      return 'We will process the shipment and submit the claim to your insurance.';
    }
  }

  showCopayDetails() {
    this.displayCopayDetails = true;
  }

  hideCopayDetails() {
    this.displayCopayDetails = false;
  }

  onTabOpen(e: { index: number }) {
    this.copayDetailsTabs[e.index] = true;
  }

  onTabClose(e: { index: number }) {
    this.copayDetailsTabs[e.index] = false;
  }
}
